import React, { useState, useEffect } from 'react';
import { Search, Users, Calendar, Phone, MapPin, Eye, Ban, CheckCircle, AlertCircle, Download } from 'lucide-react';
import { userService } from '../services';
import { User } from '@brovicms/models';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';

const UsersList = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Fetch users data
  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const result = await userService.getUsers();
      if (result.success && result.data) {
        setUsers(result.data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: Date | string | any) => {
    if (!date) return 'Never';
    try {
      const dateObj = date?.toDate ? date.toDate() : new Date(date);
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return 'Invalid date';
    }
  };

  const formatPhone = (phone: string) => {
    if (!phone) return 'Not provided';
    // Handle international format like +919123456789
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length >= 10) {
      // Format as (XXX) XXX-XXXX for US numbers, or show as-is for international
      if (cleaned.length === 10) {
        return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
      }
      // For international numbers, show with country code
      return phone;
    }
    return phone;
  };

  const handleBlockUser = async (userId: string, reason: string) => {
    if (!reason.trim()) {
      toast.error('Please provide a reason for blocking the user');
      return;
    }
    
    try {
      setActionLoading(userId);
      await userService.updateUser(userId, { 
        isBlocked: true,
        isActive: false,
        blockReason: reason.trim(),
        blockedAt: new Date().toISOString()
      } as any);
      toast.success('User blocked successfully');
      fetchUsers();
      setShowBlockModal(false);
      setBlockReason('');
    } catch (error) {
      console.error('Error blocking user:', error);
      toast.error('Failed to block user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnblockUser = async (userId: string) => {
    try {
      setActionLoading(userId);
      await userService.updateUser(userId, { 
        isBlocked: false,
        isActive: true,
        blockReason: null,
        blockedAt: null
      } as any);
      toast.success('User unblocked successfully');
      fetchUsers();
    } catch (error) {
      console.error('Error unblocking user:', error);
      toast.error('Failed to unblock user');
    } finally {
      setActionLoading(null);
    }
  };


  // Filter and search logic
  const filteredUsers = users.filter(user => {
    const userName = (user as any).name || user.displayName || '';
    const userEmail = user.email || '';
    const userCenter = (user as any).centerName || user.center || '';
    
    const matchesSearch = searchTerm === '' || 
      userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      userEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      userCenter.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || 
      (filterStatus === 'active' && ((user as any).isActive === true && (user as any).isBlocked !== true)) ||
      (filterStatus === 'blocked' && (user as any).isBlocked === true) ||
      (filterStatus === 'verified' && ((user as any).agreedToTerms === true || user.isEmailVerified === true)) ||
      (filterStatus === 'unverified' && ((user as any).agreedToTerms !== true && user.isEmailVerified !== true));
    
    return matchesSearch && matchesStatus;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const exportToExcel = async () => {
    if (exportLoading || filteredUsers.length === 0) return;
    
    try {
      setExportLoading(true);
      
      // Prepare data for Excel export
      const excelData = filteredUsers.map((user) => {
        const userName = (user as any).name || user.displayName || 'No Name';
        const userEmail = user.email || '';
        const userPhone = (user as any).mobile || user.phoneNumber || '';
        const userCenter = (user as any).centerName || user.center || '';
        const isBlocked = (user as any).isBlocked === true;
        const isActive = (user as any).isActive === true;
        
        // Determine status
        let status = 'Inactive';
        if (isBlocked) {
          status = 'Blocked';
        } else if (isActive) {
          status = 'Active';
        }

        // Format dates
        const formatDateForExcel = (date: Date | string | any) => {
          if (!date) return 'Never';
          try {
            const dateObj = date?.toDate ? date.toDate() : new Date(date);
            return dateObj.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            });
          } catch {
            return 'Invalid date';
          }
        };

        return {
          'User ID': user.id,
          'Name': userName,
          'Center': userCenter || 'N/A',
          'Phone': userPhone || 'Not provided',
          'Email': userEmail,
          'Joined Date': formatDateForExcel((user as any).createdAt || user.createdAt),
          'Agreed to Terms': (user as any).agreedToTerms ? 'Yes' : 'No',
          'Blocked': isBlocked ? 'Yes' : 'No',
          'Block Reason': (user as any).blockReason || '',
          'Blocked At': (user as any).blockedAt ? formatDateForExcel((user as any).blockedAt) : '',
        };
      });

      // Create workbook and worksheet
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Users');

      // Generate filename with current date
      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `users_export_${dateStr}.xlsx`;

      // Write file
      XLSX.writeFile(workbook, filename);
      
      toast.success(`Exported ${excelData.length} users to Excel`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Failed to export users to Excel');
    } finally {
      setExportLoading(false);
    }
  };


  const getStatusBadge = (user: User) => {
    // Priority: Blocked status overrides everything
    const isBlocked = (user as any).isBlocked === true;
    const isActive = (user as any).isActive === true;
    
    if (isBlocked) {
      return <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">Blocked</span>;
    }
    if (isActive) {
      return <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">Active</span>;
    }
    return <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-full">Inactive</span>;
  };


  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <span className="ml-3 text-gray-600">Loading users...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-heading text-primary-800">Users</h1>
          <p className="text-gray-600 mt-1">
            Manage your user base and permissions
          </p>
        </div>
        <button
          onClick={exportToExcel}
          className="btn btn-primary flex items-center gap-2"
          disabled={filteredUsers.length === 0 || exportLoading}
        >
          {exportLoading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Exporting...
            </>
          ) : (
            <>
              <Download className="w-4 h-4" />
              Export to Excel
            </>
          )}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-apple">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Total Users</p>
                <p className="text-lg font-semibold text-gray-900">{users.length}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-apple">
                <AlertCircle className="w-5 h-5 text-yellow-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Blocked Users</p>
                <p className="text-lg font-semibold text-gray-900">
                  {users.filter(user => (user as any).isBlocked || false).length}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-apple">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Verified Users</p>
                <p className="text-lg font-semibold text-gray-900">
                  {users.filter(user => (user as any).agreedToTerms === true).length}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search users by name, email, center name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input pl-10 w-full"
                />
              </div>
            </div>
            
            
            {/* Status Filter */}
            <div className="lg:w-48">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="input w-full"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="blocked">Blocked</option>
                <option value="verified">Verified</option>
                <option value="unverified">Unverified</option>
              </select>
            </div>
          </div>
          
          {/* Results Count and Items Per Page */}
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="text-sm text-gray-600">
              Showing {startIndex + 1}-{Math.min(endIndex, filteredUsers.length)} of {filteredUsers.length} users
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Items per page:</label>
              <select
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                className="input w-20 text-sm"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="card">
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Contact</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedUsers.map((user) => {
                  const userName = (user as any).name || user.displayName || 'No Name';
                  const userEmail = user.email || '';
                  const userPhone = (user as any).mobile || user.phoneNumber || '';
                  const userCenter = (user as any).centerName || user.center || '';
                  
                  return (
                  <tr key={user.id} className={(user as any).isBlocked ? 'bg-red-50' : ''}>
                    <td>
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                          <span className="text-sm font-medium text-blue-700">
                            {userName.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {userName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {userEmail || `ID: ${user.id.slice(0, 8)}...`}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="space-y-1">
                        {userPhone && (
                          <div className="flex items-center text-sm text-gray-500">
                            <Phone className="w-4 h-4 mr-2" />
                            {formatPhone(userPhone)}
                          </div>
                        )}
                        {userCenter && (
                          <div className="flex items-center text-sm text-gray-500">
                            <MapPin className="w-4 h-4 mr-2" />
                            {userCenter}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar className="w-4 h-4 mr-2" />
                        {formatDate((user as any).createdAt || user.createdAt)}
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center space-x-2">
                        <button 
                          onClick={() => {
                            setSelectedUser(user);
                            setShowUserModal(true);
                          }}
                          className="btn btn-ghost btn-sm"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        
                        {!(user as any).isBlocked ? (
                          <button 
                            onClick={() => {
                              setSelectedUser(user);
                              setShowBlockModal(true);
                            }}
                            className="btn btn-ghost btn-sm text-red-600 hover:bg-red-50"
                            title="Block User"
                            disabled={actionLoading === user.id}
                          >
                            {actionLoading === user.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                            ) : (
                              <Ban className="w-4 h-4" />
                            )}
                          </button>
                        ) : (
                          <button 
                            onClick={() => handleUnblockUser(user.id)}
                            className="btn btn-ghost btn-sm text-green-600 hover:bg-green-50"
                            title="Unblock User"
                            disabled={actionLoading === user.id}
                          >
                            {actionLoading === user.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                            ) : (
                              <CheckCircle className="w-4 h-4" />
                            )}
                          </button>
                        )}
                        
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="btn btn-ghost btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`btn btn-sm ${
                            currentPage === pageNum
                              ? 'btn-primary'
                              : 'btn-ghost'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="btn btn-ghost btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Block User Confirmation Modal */}
      {showBlockModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3">
                <Ban className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Block User</h3>
                <p className="text-sm text-gray-600">This action will prevent the user from accessing the platform</p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-sm text-gray-700 mb-4">
                Are you sure you want to block <strong>{(selectedUser as any).name || selectedUser.displayName || selectedUser.email}</strong>?
              </p>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for blocking <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                  placeholder="Enter the reason for blocking this user..."
                  rows={4}
                  className="input w-full resize-none"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  This reason will be recorded and visible in the user's profile.
                </p>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-xs text-yellow-800">
                  <strong>Note:</strong> The user will be unable to log in and access their account until unblocked.
                </p>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowBlockModal(false);
                  setBlockReason('');
                }}
                className="btn btn-secondary"
                disabled={actionLoading === selectedUser.id}
              >
                Cancel
              </button>
              <button
                onClick={() => handleBlockUser(selectedUser.id, blockReason)}
                className="btn btn-danger"
                disabled={actionLoading === selectedUser.id || !blockReason.trim()}
              >
                {actionLoading === selectedUser.id ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Blocking...
                  </div>
                ) : (
                  'Block User'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Details Modal */}
      {showUserModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                  <span className="text-lg font-medium text-blue-700">
                    {((selectedUser as any).name || selectedUser.displayName || 'U').charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {(selectedUser as any).name || selectedUser.displayName || 'User Details'}
                  </h3>
                  <p className="text-sm text-gray-500">{selectedUser.email}</p>
                </div>
              </div>
              <button
                onClick={() => setShowUserModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              {/* Status Section */}
              <div className="border-b pb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Status</h4>
                <div className="space-y-2">
                  {getStatusBadge(selectedUser)}
                  {(selectedUser as any).isBlocked && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-start">
                        <AlertCircle className="w-5 h-5 text-red-600 mr-2 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-red-900">User is Blocked</p>
                          {(selectedUser as any).blockedAt && (
                            <p className="text-xs text-red-700 mt-1">
                              Blocked on: {formatDate((selectedUser as any).blockedAt)}
                            </p>
                          )}
                          {(selectedUser as any).blockReason && (
                            <div className="mt-2">
                              <p className="text-xs font-medium text-red-900">Reason:</p>
                              <p className="text-xs text-red-700 mt-1">
                                {(selectedUser as any).blockReason}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* User Information */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-500">Name</label>
                  <p className="text-sm text-gray-900 mt-1">
                    {(selectedUser as any).name || selectedUser.displayName || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Email</label>
                  <p className="text-sm text-gray-900 mt-1">{selectedUser.email || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Phone</label>
                  <p className="text-sm text-gray-900 mt-1">
                    {formatPhone((selectedUser as any).mobile || selectedUser.phoneNumber || '')}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Center</label>
                  <p className="text-sm text-gray-900 mt-1">
                    {(selectedUser as any).centerName || selectedUser.center || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Joined</label>
                  <p className="text-sm text-gray-900 mt-1">
                    {formatDate((selectedUser as any).createdAt || selectedUser.createdAt)}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Last Login</label>
                  <p className="text-sm text-gray-900 mt-1">
                    {formatDate((selectedUser as any).lastLoginAt || selectedUser.lastLoginAt)}
                  </p>
                </div>
                {(selectedUser as any).agreedToTerms !== undefined && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Agreed to Terms</label>
                    <p className="text-sm text-gray-900 mt-1">
                      {(selectedUser as any).agreedToTerms ? 'Yes' : 'No'}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowUserModal(false)}
                className="btn btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersList;
